/**
 * Omron (Sentech) STC-series industrial cameras.
 *
 * Unlike Basler, this vendor publishes a full specification table on a page its
 * robots.txt allows, so these are TRANSCRIBED figures rather than derived ones:
 * width, height, sensor model, pixel size, shutter type and frame rate all come
 * from Omron's own table. Only `/downloads/`, `*.pdf`, `/fr/api/`, `/fr/misc/`
 * and `/fr/search` are disallowed, and none of those were touched.
 *
 * The part numbers are only partly self-describing — STC-MBS500POE reads as
 * monochrome, 5 MP, GigE — and stop short of what the selector actually needs.
 * Megapixels do not give a width without an aspect ratio, and the frame rate is
 * not in the name at all. So there is no decode() here: guessing 2448 px from
 * "500" would be a fabricated number in a field the field-of-view calculation
 * depends on. The table is the source, and a model not in it is not adoptable.
 *
 * Shutter type is the reason this vendor is worth having. Omron states it per
 * model, and on a moving belt it decides whether an image is usable at all — a
 * rolling shutter reads rows at different moments, so a part travelling past
 * comes out skewed. Twelve of the sixty models below are rolling, and the
 * selector now excludes them by default.
 *
 * Prices are not here for the same reason as Basler: they run through the
 * channel, not the website.
 *
 * Source: https://industrial.omron.eu/en/products/area-scan (retrieved 2026-07-20)
 */
const DOCS = 'https://industrial.omron.eu/en/products/area-scan';
/** One row of Omron's table. Positional on purpose — it mirrors the source. */
function m(model, colour, iface, shutter, widthPx, heightPx, sensor, pixelSizeUm, fps) {
    return {
        vendor: 'Omron',
        model,
        family: 'STC',
        scan: 'area',
        widthPx,
        heightPx,
        speed: fps,
        speedUnit: 'fps',
        interface: iface,
        colour,
        // Omron does not publish an IP rating for the STC series on this page.
        ingressProtection: null,
        spectrum: 'visible',
        tier: null,
        shutter,
        mount: 'C-mount',
        sensor,
        pixelSizeUm,
        // GenICam/GigE Vision, so the standard driver serves it. No vendor SDK on
        // the station.
        driver: 'genicam',
        docsUrl: DOCS,
    };
}
export const OMRON_CAMERAS = [
    m('STC-MBS503POE-HS', 'mono', 'GigE', 'rolling', 2592, 1944, 'AR0521', 2.2, 21.8),
    m('STC-MBS122BPOE', 'mono', 'GigE', 'global', 4096, 3000, 'IMX304', 3.45, 8.9),
    m('STC-MBS122BU3V', 'mono', 'USB3', 'global', 4096, 3000, 'IMX304', 3.45, 23.5),
    m('STC-MBS123BU3V', 'mono', 'USB3', 'global', 4096, 3000, 'IMX253', 3.45, 30.4),
    m('STC-MBS1242POE', 'mono', 'GigE', 'rolling', 4000, 3000, 'IMX226', 1.85, 9.2),
    m('STC-MBS1242U3V', 'mono', 'USB3', 'rolling', 4000, 3000, 'IMX226', 1.85, 31.2),
    m('STC-MBS163POE', 'mono', 'GigE', 'global', 1440, 1088, 'IMX273', 3.45, 70.7),
    m('STC-MBS163U3V', 'mono', 'USB3', 'global', 1440, 1080, 'IMX273', 3.45, 238.0),
    m('STC-MBS202POE', 'mono', 'GigE', 'global', 1624, 1240, 'IMX430', 4.5, 54.6),
    m('STC-MBS2041POE', 'mono', 'GigE', 'rolling', 5472, 3648, 'IMX183', 2.4, 5.5),
    m('STC-MBS2041U3V', 'mono', 'USB3', 'rolling', 5472, 3648, 'IMX183', 2.40, 18.5),
    m('STC-MBS231POE', 'mono', 'GigE', 'global', 1920, 1200, 'IMX249', 5.86, 41.3),
    m('STC-MBS231U3V', 'mono', 'USB3', 'global', 1920, 1200, 'IMX249', 5.86, 41.6),
    m('STC-MBS241U3V', 'mono', 'USB3', 'global', 1920, 1200, 'IMX174', 5.86, 163.0),
    m('STC-MBS312POE', 'mono', 'GigE', 'global', 2048, 1536, 'IMX265', 3.45, 34.9),
    m('STC-MBS312U3V', 'mono', 'USB3', 'global', 2048, 1536, 'IMX265', 3.45, 56.0),
    m('STC-MBS322U3V', 'mono', 'USB3', 'global', 2048, 1536, 'IMX252', 3.45, 121.0),
    m('STC-MBS43POE', 'mono', 'GigE', 'global', 720, 540, 'IMX287', 6.90, 282.8),
    m('STC-MBS43U3V', 'mono', 'USB3', 'global', 720, 540, 'IMX287', 6.9, 527.1),
    m('STC-MBS500POE', 'mono', 'GigE', 'global', 2448, 2048, 'IMX264', 3.45, 21.9),
    m('STC-MBS500U3V', 'mono', 'USB3', 'global', 2448, 2048, 'IMX264', 3.45, 35.8),
    m('STC-MBS510U3V', 'mono', 'USB3', 'global', 2448, 2048, 'IMX250', 3.45, 75.7),
    m('STC-MBS52POE', 'mono', 'GigE', 'global', 816, 624, 'IMX433', 9.0, 166.5),
    m('STC-MBS52U3V', 'mono', 'USB3', 'global', 816, 624, 'IMX426', 9.0, 721.5),
    m('STC-MBS642POE', 'mono', 'GigE', 'rolling', 3072, 2048, 'IMX178', 2.4, 17.4),
    m('STC-MBS70BU3V', 'mono', 'USB3', 'global', 3216, 2208, 'IMX428', 4.5, 51.2),
    m('STC-MBS881POE', 'mono', 'GigE', 'global', 4096, 2160, 'IMX267', 3.45, 12.4),
    m('STC-MBS891U3V', 'mono', 'USB3', 'global', 4096, 2160, 'IMX255', 3.45, 41.9),
    m('STC-MCS503POE-HS', 'colour', 'GigE', 'rolling', 2592, 1944, 'AR0521', 2.2, 21.8),
    m('STC-MCCM401U3V', 'colour', 'USB3', 'global', 2048, 2048, 'CMV4000', 5.5, 89.0),
    m('STC-MCE132POE', 'colour', 'GigE', 'global', 1280, 1024, 'EV76C560', 5.3, 61.0),
    m('STC-MCE132U3V', 'colour', 'USB3', 'global', 1280, 1024, 'EV76C560', 5.3, 60.0),
    m('STC-MCS122BPOE', 'colour', 'GigE', 'global', 4096, 3000, 'IMX304', 3.45, 8.9),
    m('STC-MCS123BU3V', 'colour', 'USB3', 'global', 4096, 3000, 'IMX253', 3.45, 30.4),
    m('STC-MCS1242POE', 'colour', 'GigE', 'rolling', 4000, 3000, 'IMX226', 1.85, 9.2),
    m('STC-MCS1242U3V', 'colour', 'USB3', 'rolling', 4000, 3000, 'IMX226', 1.85, 31.2),
    m('STC-MCS163POE', 'colour', 'GigE', 'global', 1440, 1088, 'IMX273', 3.45, 70.7),
    m('STC-MCS163U3V', 'colour', 'USB3', 'global', 1440, 1080, 'IMX273', 3.45, 238.0),
    m('STC-MCS202POE', 'colour', 'GigE', 'global', 1624, 1240, 'IMX430', 4.5, 54.6),
    m('STC-MCS2041POE', 'colour', 'GigE', 'rolling', 5472, 3648, 'IMX183', 2.4, 5.5),
    m('STC-MCS2041U3V', 'colour', 'USB3', 'rolling', 5472, 3648, 'IMX183', 2.40, 18.5),
    m('STC-MCS231POE', 'colour', 'GigE', 'global', 1920, 1200, 'IMX249', 5.86, 41.3),
    m('STC-MCS231U3V', 'colour', 'USB3', 'global', 1920, 1200, 'IMX249', 5.86, 41.6),
    m('STC-MCS241U3V', 'colour', 'USB3', 'global', 1920, 1200, 'IMX174', 5.86, 163.0),
    m('STC-MCS312POE', 'colour', 'GigE', 'global', 2048, 1536, 'IMX265', 3.45, 34.9),
    m('STC-MCS312U3V', 'colour', 'USB3', 'global', 2048, 1536, 'IMX265', 3.45, 56.0),
    m('STC-MCS322U3V', 'colour', 'USB3', 'global', 2048, 1536, 'IMX252', 3.45, 121.0),
    m('STC-MCS43POE', 'colour', 'GigE', 'global', 720, 540, 'IMX287', 6.90, 282.8),
    m('STC-MCS43U3V', 'colour', 'USB3', 'global', 720, 540, 'IMX287', 6.9, 527.1),
    m('STC-MCS500POE', 'colour', 'GigE', 'global', 2448, 2048, 'IMX264', 3.45, 21.9),
    m('STC-MCS500U3V', 'colour', 'USB3', 'global', 2448, 2048, 'IMX264', 3.45, 35.8),
    m('STC-MCS502CXP6', 'colour', 'CoaXPress', 'global', 2448, 2048, null, 2.74, 103),
    m('STC-MCS510CXP6', 'colour', 'CoaXPress', 'global', 2448, 2048, 'IMX250', 3.45, 98),
    m('STC-MCS510U3V', 'colour', 'USB3', 'global', 2448, 2048, 'IMX250', 3.45, 75.7),
    m('STC-MCS52POE', 'colour', 'GigE', 'global', 816, 624, 'IMX433', 9.0, 166.5),
    m('STC-MCS52U3V', 'colour', 'USB3', 'global', 816, 624, 'IMX426', 9.0, 721.5),
    m('STC-MCS642POE', 'colour', 'GigE', 'rolling', 3072, 2048, 'IMX178', 2.4, 17.4),
    m('STC-MCS70BU3V', 'colour', 'USB3', 'global', 3216, 2208, 'IMX428', 4.5, 51.2),
    m('STC-MCS881POE', 'colour', 'GigE', 'global', 4096, 2160, 'IMX267', 3.45, 12.4),
    m('STC-MCS891U3V', 'colour', 'USB3', 'global', 4096, 2160, 'IMX255', 3.45, 41.9),
];
//# sourceMappingURL=omron.js.map